import { Formik, FormikHelpers } from "formik";
import { useEffect, useState } from "react";
import { Button, Card, Col, Container, Form, Row } from "react-bootstrap";
import ReactQuill from "react-quill";
import * as Yup from "yup";

import PlansApi from "../../api/PlansApi";
import PageHeader from "../../components/PageHeader";
import ScrollToFirstError from "../../components/ScrollToFirstError";
import CardLoader from "../../components/content-loader/CardLoader";
import { ButtonSpinner } from "../../components/placeholders";
import { QuillModules } from "../../config/app-constants";
import { EToastvariant, IPricingPageSettingsForm } from "../../config/interfaces";
import { useAppDispatch } from "../../redux/Store";
import { showToastMsg } from "../../redux/slice/ToastMsgSlice";
import { UtilService } from "../../services";

const INIT_UF_VALUES: IPricingPageSettingsForm = {
  pricingTitle: "",
  pricingSubTitle: "",
  footerContent: "",
  pricingTitleUuId: "",
  pricingSubTitleUuId: "",
  footerContentUuId: ""
};

const INITIAL_FORM_SCHEMA = {
  pricingTitle: Yup.string()
    .trim()
    .min(3, "Please enter at least 3 characters")
    .max(255, "Plane Title must be less than 255 characters")
    .required("Plane Title is required"),
  pricingSubTitle: Yup.string()
    .trim()
    .min(3, "Please enter at least 3 characters")
    .max(255, "Plan Subtitle must be less than 255 characters")
    .notRequired(),
  footerContent: Yup.string().trim().notRequired()
};

const PricingPageSettings = () => {
  const dispatch = useAppDispatch();
  const [isFetching, setIsFetching] = useState<boolean>(true);
  const [initialFormValues, setInitialFormValues] = useState(INIT_UF_VALUES);
  const [initialFormSchema, setInitialFormSchema] = useState(
    Yup.object().shape(INITIAL_FORM_SCHEMA)
  );

  useEffect(() => {
    getPricingPageSettingDetails();
  }, []);

  const getPricingPageSettingDetails = async () => {
    try {
      setIsFetching(true);
      const _values = {
        keys: ["PRICING_PAGE_TITLE", "PRICING_PAGE_SUB_TITLE", "PRICING_PAGE_FOOTER"]
      };
      const plansApi = PlansApi.getInstance();
      const response = await plansApi.getPricingPageSettingsByKey(_values);
      const _data = response?.data?.records;
      const _pricingPageSettingData = {
        pricingTitle: _data.find((q: any) => q.key === "PRICING_PAGE_TITLE").value || "",
        pricingTitleUuId: _data.find((q: any) => q.key === "PRICING_PAGE_TITLE").uuid || "",
        pricingSubTitle: _data.find((q: any) => q.key === "PRICING_PAGE_SUB_TITLE").value || "",
        pricingSubTitleUuId: _data.find((q: any) => q.key === "PRICING_PAGE_SUB_TITLE").uuid || "",
        footerContent: _data.find((q: any) => q.key === "PRICING_PAGE_FOOTER").value || "",
        footerContentUuId: _data.find((q: any) => q.key === "PRICING_PAGE_FOOTER").uuid || ""
      };
      setInitialFormValues(() => _pricingPageSettingData);
    } catch (error) {
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Error",
          message: UtilService.getErrorMessage(error),
          variant: EToastvariant.Danger
        })
      );
    } finally {
      setIsFetching(false);
    }
  };

  const handleAddEditFormSubmit = async (
    values: IPricingPageSettingsForm,
    { setSubmitting, resetForm }: FormikHelpers<IPricingPageSettingsForm>
  ) => {
    try {
      setSubmitting(true);
      let _msg = `${values?.pricingTitle} added successfully`;
      let pricingTitleUuId = initialFormValues?.pricingTitleUuId;
      let pricingSubTitleUuId = initialFormValues?.pricingSubTitleUuId;
      let footerContentUuId = initialFormValues?.footerContentUuId;
      let _values: any = [
        {
          "key": "PRICING_PAGE_TITLE",
          "value": values.pricingTitle
        },
        {
          "key": "PRICING_PAGE_SUB_TITLE",
          "value": values.pricingSubTitle
        },
        {
          "key": "PRICING_PAGE_FOOTER",
          "value": values.footerContent
        }
      ];
      const plansApi = PlansApi.getInstance();
      if (pricingTitleUuId || pricingSubTitleUuId || footerContentUuId) {
        await plansApi.updatePricingPageSettings(_values);
        _msg = `Changes applied to ${values?.pricingTitle} successfully`;
      } else {
        await plansApi.createPricingPageSettings(_values);
        resetForm();
      }
      getPricingPageSettingDetails();
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Success",
          message: _msg,
          variant: EToastvariant.Success
        })
      );
    } catch (error) {
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Error",
          message: UtilService.getErrorMessage(error),
          variant: EToastvariant.Danger
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Container className="w-800">
        <PageHeader title="Pricing page settings" />

        {isFetching ? (
          <CardLoader cardType="details" />
        ) : (
          <Formik
            enableReinitialize={true}
            initialValues={initialFormValues}
            validationSchema={initialFormSchema}
            onSubmit={handleAddEditFormSubmit}
          >
            {({
              handleBlur,
              handleChange,
              handleSubmit,
              setFieldValue,
              setTouched,
              validateForm,
              values,
              errors,
              touched,
              isSubmitting,
              submitCount,
              isValid
            }) => (
              <>
                <Form
                  onSubmit={async (event) => {
                    event.preventDefault();
                    await validateForm();
                    handleSubmit();
                  }}
                >
                  <ScrollToFirstError submitCount={submitCount} isValid={isValid} errors={errors} />
                  <Card className="form-card">
                    <Card.Body>
                      <Row className="align-items-center">
                        <Col sm={4}>
                          <Form.Label className="required-field inline-label">
                            Page Title
                          </Form.Label>
                        </Col>
                        <Col>
                          <Form.Group className="form-group" controlId="pricingTitle">
                            <Form.Control
                              type="text"
                              autoComplete="false"
                              placeholder=""
                              maxLength={255}
                              name="pricingTitle"
                              value={values.pricingTitle}
                              onBlur={handleBlur("pricingTitle")}
                              onChange={handleChange("pricingTitle")}
                              isInvalid={Boolean(errors.pricingTitle) && touched.pricingTitle}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.pricingTitle}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row className="align-items-center">
                        <Col sm={4}>
                          <Form.Label className="inline-label">Pricing Subtitle</Form.Label>
                        </Col>
                        <Col>
                          <Form.Group className="form-group" controlId="pricingSubTitle">
                            <Form.Control
                              type="text"
                              autoComplete="false"
                              placeholder=""
                              maxLength={255}
                              name="pricingSubTitle"
                              value={values.pricingSubTitle}
                              onBlur={handleBlur("pricingSubTitle")}
                              onChange={handleChange("pricingSubTitle")}
                              isInvalid={Boolean(errors.pricingSubTitle) && touched.pricingSubTitle}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.pricingSubTitle}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <Form.Group className="form-group" controlId="footerContent">
                            <Form.Label>Page footer info (below pricing plans)</Form.Label>
                            <ReactQuill
                              theme="snow"
                              className="h-250"
                              modules={QuillModules}
                              value={values.footerContent}
                              onChange={handleChange("footerContent")}
                              placeholder="Enter information"
                            />
                            {Boolean(errors.footerContent) && touched.footerContent && (
                              <span className="field-error invalid">{errors.footerContent}</span>
                            )}
                          </Form.Group>
                        </Col>
                      </Row>
                      <div className="form-btns-wrap">
                        {/* <Button
                          size="lg"
                          variant={uUId ? "outline-danger" : "outline-greyDark"}
                          className="btn-w-120"
                          onClick={() => {
                            uUId ? navigate(`/assisted-filings/${uUId}`) : navigate(-1);
                          }}
                        >
                          Cancel
                        </Button> */}
                        <Button
                          size="lg"
                          variant="primary"
                          className="btn-w-160"
                          type="submit"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? <ButtonSpinner /> : "Save"}
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Form>
              </>
            )}
          </Formik>
        )}
      </Container>
    </>
  );
};

export default PricingPageSettings;
